import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { useBeautyProduct } from '../../../beauty-shared/context/BeautyProductContext';
import {
  StudioLookPickerSubTabs,
  StudioOptionsFullViewModal,
  StudioOptionsFullViewTrigger,
} from '../../../beauty-shared/components/StudioOptionsFullViewModal';
import '../../../beauty-shared/styles/StudioOptionsFullViewModal.css';
import LooksActionsRail from '../../looks/components/LooksActionsRail';
import LooksCompareBoard from '../../looks/components/LooksCompareBoard';
import '../../looks/pages/LooksStudio.css';
import MakeupAiLoader from '../../makeuphair/components/MakeupAiLoader';
import { finishLoaderUi } from '../../makeuphair/components/makeupLoaderCopy';
import StudioPhotoSourceModal from '../../makeuphair/components/StudioPhotoSourceModal';
import { getSalonFaceGender } from '../../salon/utils/salonFaceSession';
import { SHELL_MQ } from '../../shared/tabletMedia';
import { renderGuestQrDataUrl } from '../../shared/guestQrCode';
import { ensureJpegDataUrl } from '../../shared/imageRefToDataUrl';
import {
  beginFreshLookCapture,
  isHairColorHandoffFromLook,
  LOOK_MODULE_IDS,
  resolveMakeupStyleStudioEntry,
  saveLookResult,
  shouldApplyHairstyleOnCurrentLook,
} from '../../shared/lookSession';
import {
  COMPARE_ORIGINAL_ID,
  appendLookToHistory,
  handleCompareLookSelect,
  primeCompareSlotsForNewLook,
  resolveStudioDownloadImage,
} from '../../shared/compareLookHistory';
import { buildHairStyleHistoryDetails } from '../utils/hairStyleCompareHistory';
import {
  createHairDownloadUrl as createHairColorDownloadUrl,
  fetchHairColors,
  fetchHairJobStatus,
  fetchHairResultObjectUrl,
  generateHairColorLook,
} from '../../hair-coloring/utils/hairColorApi';
import {
  HAIR_APPLY_DONE as HAIR_COLOR_APPLY_DONE,
  HAIR_COLOR_APPLY_TITLE,
  hairColorApplyMessageForProgress,
  hairJobProgressFloor,
  setHairColorApplyLoaderStep,
} from '../../hair-coloring/utils/hairColorLoaderCopy';
import {
  filterStyleGroupsForHeadView,
  filterPromptStylesForHeadView,
  getPromptStyleGroups,
  getPromptStylesForGender,
  headViewDisplayLabel,
  headViewFromProfile,
  recommendPromptStyles,
  styleFitsHeadView,
  targetHairLengthForStyle,
} from '../data/promptStyleCatalog';
import {
  PROMPT_HAIR_COLORS,
  buildPromptWithHairColor,
  matchPromptHairColorFromProfile,
} from '../data/promptHairColors';
import {
  createHairDownloadUrl,
  generateHairPromptLook,
  hairStyleThumbUrl,
  promptStyleThumbFallbackUrl,
  promptStyleThumbUrl,
  recommendHairLooks,
} from '../utils/hairStylingApi';
import {
  HAIR_APPLY_DONE,
  HAIR_RECOMMEND_DONE,
  HAIR_RECOMMEND_TITLE,
  HAIR_STYLE_APPLY_TITLE,
  hairRecommendMessageForProgress,
  hairStyleApplyMessageForProgress,
  setHairRecommendLoaderStep,
  setHairStyleApplyLoaderStep,
} from '../utils/hairLoaderCopy';
import '../../makeuphair/components/MakeupAiLoader.css';
import './HairStylingStudio.css';

const FALLBACK_COLOR_TECHNIQUES = [
  { id: 'global', name: 'Global Color' },
  { id: 'root_touch_up', name: 'Root Touch-Up' },
  { id: 'highlights', name: 'Highlights' },
  { id: 'babylights', name: 'Babylights' },
  { id: 'balayage', name: 'Balayage' },
  { id: 'ombre', name: 'Ombre' },
  { id: 'sombre', name: 'Sombre' },
  { id: 'money_piece', name: 'Money Piece' },
  { id: 'lowlights', name: 'Lowlights' },
  { id: 'root_shadow', name: 'Root Shadow' },
  { id: 'color_melt', name: 'Color Melt' },
  { id: 'peekaboo', name: 'Peekaboo' },
  { id: 'foilyage', name: 'Foilyage' },
];

const TECHNIQUES_NEED_BASE_TARGET = new Set([
  'ombre',
  'sombre',
  'balayage',
  'root_shadow',
  'color_melt',
  'lowlights',
  'root_touch_up',
  'foilyage',
]);

const STYLE_TABS = [
  { id: 'recommended', label: 'Recommended' },
  { id: 'catalog', label: 'All styles' },
];

const APPLIED_LOOK_HISTORY_LIMIT = 16;

const STATUS_LABELS = {
  queued: 'Queued',
  waiting_for_gpu: 'Waiting for AI processor',
  loading_model: 'Preparing hairstyle',
  processing: 'Creating your look',
  enhancing: 'Enhancing image',
  uploading: 'Saving result',
  completed: 'Completed',
  failed: 'Could not complete',
};

const HAIR_KIOSK_MQ = [
  SHELL_MQ,
  '(orientation: portrait) and (max-width: 1366px)',
  '(orientation: landscape) and (max-width: 1400px) and (max-height: 920px)',
].join(', ');

function normalizeGender(raw) {
  const g = String(raw || '').trim().toLowerCase();
  if (['m', 'male', 'man', 'men', 'boy'].includes(g)) return 'men';
  if (['f', 'female', 'woman', 'women', 'girl'].includes(g)) return 'women';
  return '';
}

function useKioskViewport() {
  const [match, setMatch] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia(HAIR_KIOSK_MQ).matches : false,
  );
  useEffect(() => {
    const mq = window.matchMedia(HAIR_KIOSK_MQ);
    const sync = () => setMatch(mq.matches);
    sync();
    mq.addEventListener?.('change', sync);
    return () => mq.removeEventListener?.('change', sync);
  }, []);
  return match;
}

export default function HairStylingStudio({ forcedFocus = '' }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { appPath, service, title: productTitle } = useBeautyProduct();
  const isKioskViewport = useKioskViewport();
  const panelDragRef = useRef({ startY: 0, dragging: false, moved: false });
  const studioFocus =
    forcedFocus === 'color' || forcedFocus === 'style' ? forcedFocus : 'style';
  const tabs = studioFocus === 'color' ? [] : STYLE_TABS;

  const seededGender = useMemo(
    () =>
      normalizeGender(
        location.state?.gender ||
          location.state?.userData?.gender ||
          getSalonFaceGender() ||
          '',
      ),
    [location.state],
  );

  const [resolvedGender, setResolvedGender] = useState(seededGender);
  const [genderSource, setGenderSource] = useState(seededGender ? 'session' : '');
  const [tab, setTab] = useState(studioFocus === 'color' ? 'color' : 'recommended');
  const [panelOpen, setPanelOpen] = useState(true);

  const [sourceImage, setSourceImage] = useState(() => {
    const entry = resolveMakeupStyleStudioEntry(location.state);
    return entry.displayImage || '';
  });
  const [resultImage, setResultImage] = useState('');
  // Locked capture — every new style must start from this, never from a prior result.
  const originalCaptureRef = useRef('');
  if (!originalCaptureRef.current) {
    originalCaptureRef.current = String(
      resolveMakeupStyleStudioEntry(location.state).applyOriginal || location.state?.image || '',
    );
  }
  const handoffApplyBaseRef = useRef('');
  const handoffStyleBaseRef = useRef('');
  if (!handoffApplyBaseRef.current && studioFocus === 'color' && isHairColorHandoffFromLook(location.state)) {
    handoffApplyBaseRef.current = String(location.state?.image || '');
  }
  if (
    !handoffStyleBaseRef.current
    && studioFocus === 'style'
    && shouldApplyHairstyleOnCurrentLook(location.state)
  ) {
    handoffStyleBaseRef.current = String(location.state?.image || '');
  }
  const getOriginalCapture = useCallback(
    () => originalCaptureRef.current || sourceImage || '',
    [sourceImage],
  );
  const getStyleApplyBase = useCallback(
    () => originalCaptureRef.current || getOriginalCapture() || '',
    [getOriginalCapture],
  );
  const getColorApplyBase = useCallback(
    () => originalCaptureRef.current || getOriginalCapture() || '',
    [getOriginalCapture],
  );
  const buildGeneratePayload = useCallback(
    (fields) => {
      const capture = getOriginalCapture();
      return {
        ...fields,
        image: capture,
        original_image: capture,
        customer_original_image: capture,
        base_image_mode: 'original_capture',
        style_flux_only: true,
        // Prompt FLUX full replace; polish = shoulder cleanup only (no face-core seam).
        flux_face_relock: false,
        flux_light_polish: true,
      };
    },
    [getOriginalCapture],
  );
  const [styles, setStyles] = useState([]);
  const [colors, setColors] = useState([]);
  const [colorTechniques, setColorTechniques] = useState(FALLBACK_COLOR_TECHNIQUES);
  const [colorTechnique, setColorTechnique] = useState('global');
  const [selectedStyle, setSelectedStyle] = useState(null);
  const [selectedColor, setSelectedColor] = useState(null);
  const [selectedColors, setSelectedColors] = useState([]);
  const [multiColorEnabled, setMultiColorEnabled] = useState(false);
  const [multiColorConfirmOpen, setMultiColorConfirmOpen] = useState(false);
  const [customPrompt, setCustomPrompt] = useState('');
  const [promptHairColor, setPromptHairColor] = useState(PROMPT_HAIR_COLORS[0]);
  const [recommendations, setRecommendations] = useState([]);
  const [hairProfile, setHairProfile] = useState(null);
  const [stylesFullViewOpen, setStylesFullViewOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [busyKind, setBusyKind] = useState('');
  const [statusText, setStatusText] = useState('');
  const [pipelineDiag, setPipelineDiag] = useState(null);
  const [showPipelineDiag, setShowPipelineDiag] = useState(false);
  const [loaderProgress, setLoaderProgress] = useState(0);
  const [loaderMessage, setLoaderMessage] = useState('');
  const [loaderTitle, setLoaderTitle] = useState('');
  const jobLoaderCreepRef = useRef(null);
  const [error, setError] = useState('');
  const [photoModal, setPhotoModal] = useState(false);
  const [jobId, setJobId] = useState('');
  const [comparing, setComparing] = useState(false);
  const [appliedLookHistory, setAppliedLookHistory] = useState([]);
  const [compareLeftId, setCompareLeftId] = useState(COMPARE_ORIGINAL_ID);
  const [compareRightId, setCompareRightId] = useState(null);
  const [comparePickSlot, setComparePickSlot] = useState('right');
  const [compareMode, setCompareMode] = useState('side');
  const [comparePosition, setComparePosition] = useState(50);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [showQrModal, setShowQrModal] = useState(false);
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [qrBusy, setQrBusy] = useState(false);
  const [qrError, setQrError] = useState('');
  const [qrImageOverride, setQrImageOverride] = useState('');
  const [qrLabelOverride, setQrLabelOverride] = useState('');
  const qrTitleId = useId();
  const pendingApplyLabelRef = useRef('Hairstyle');
  const pendingApplySourceRef = useRef('style');
  const pendingApplyStyleIdRef = useRef(null);
  const pendingApplyColorIdRef = useRef(null);
  const pendingApplyColorRegionRef = useRef('full');
  const pendingApplyColorNameRef = useRef('');
  // Avoid reopening the recommend modal after the user already picked/closed
  // (duplicate recommend fetches can finish late and call setRecModalOpen(true)).
  const recModalAutoOpenedForImageRef = useRef('');
  const recModalUserClosedRef = useRef(false);

  const displayImage = resultImage || sourceImage;
  const isKioskLayout = isKioskViewport && !isFullscreen;

  const loaderIconSrc = useMemo(() => {
    if (busyKind === 'color' || (busyKind === 'recommend' && studioFocus === 'color')) {
      return '/hub/color.png';
    }
    return '/hub/hairstyle.png';
  }, [busyKind, studioFocus]);

  const clearLoaderUi = useCallback(() => {
    if (jobLoaderCreepRef.current) {
      window.clearInterval(jobLoaderCreepRef.current);
      jobLoaderCreepRef.current = null;
    }
    setLoaderProgress(0);
    setLoaderMessage('');
    setLoaderTitle('');
  }, []);

  const applyLoaderMessageFn = useCallback(
    (kind) => {
      return kind === 'color' ? hairColorApplyMessageForProgress : hairStyleApplyMessageForProgress;
    },
    [],
  );

  const beginApplyLoader = useCallback(
    (kind) => {
      const isColor = kind === 'color';
      setLoaderTitle(isColor ? HAIR_COLOR_APPLY_TITLE : HAIR_STYLE_APPLY_TITLE);
      if (isColor) setHairColorApplyLoaderStep(setLoaderProgress, setLoaderMessage, 8);
      else setHairStyleApplyLoaderStep(setLoaderProgress, setLoaderMessage, 8);
    },
    [],
  );

  const startJobLoaderCreep = useCallback(
    (kind) => {
      if (jobLoaderCreepRef.current) window.clearInterval(jobLoaderCreepRef.current);
      const msgFn = applyLoaderMessageFn(kind);
      jobLoaderCreepRef.current = window.setInterval(() => {
        setLoaderProgress((p) => {
          const next = Math.min(92, p + 1);
          setLoaderMessage(msgFn(next));
          return next;
        });
      }, 720);
    },
    [applyLoaderMessageFn],
  );

  const startBusy = (kind, label) => {
    setBusy(true);
    setBusyKind(kind);
    setError('');
    setStatusText(label || '');
  };

  const stopBusy = () => {
    clearLoaderUi();
    setBusy(false);
    setBusyKind('');
    setStatusText('');
  };

  const rememberAppliedLook = useCallback((image, { label, source, styleId = null, colorId = null, colorRegion: region = null, colorName = '' } = {}) => {
    if (!image) return null;
    const historyCopy = buildHairStyleHistoryDetails({
      label,
      source,
      styleId,
      colorId,
      colorRegion: region,
      colorName,
    });
    let entryId = '';
    setAppliedLookHistory((prev) => {
      const next = appendLookToHistory(
        prev,
        {
          image,
          label: historyCopy.label,
          idPrefix: 'hair',
          extra: {
            source: source || 'style',
            styleId: styleId != null ? String(styleId) : null,
            colorId: colorId != null ? String(colorId) : null,
            colorRegion: region != null ? String(region) : null,
            detailTitle: historyCopy.detailTitle,
            detailLines: historyCopy.detailLines,
          },
        },
        APPLIED_LOOK_HISTORY_LIMIT,
      );
      entryId = next[0]?.id || '';
      return next;
    });
    if (entryId) {
      primeCompareSlotsForNewLook(entryId, {
        setCompareLeftId,
        setCompareRightId,
        setComparePickSlot,
      });
    }
    if (!colorId && studioFocus === 'style') {
      saveLookResult(LOOK_MODULE_IDS.HAIRSTYLE, image).catch(() => {});
    }
    if (colorId && studioFocus === 'color') {
      saveLookResult(LOOK_MODULE_IDS.HAIRCOLOR, image).catch(() => {});
    }
    return entryId ? { id: entryId, image, label, source, styleId, colorId, colorRegion: region } : null;
  }, [studioFocus]);

  useEffect(() => {
    const capture = location.state?.image ? String(location.state.image) : '';
    if (!capture) return;
    let cancelled = false;
    const entry = resolveMakeupStyleStudioEntry(location.state);
    const rawApply = entry.applyOriginal || capture;
    const rawDisplay = entry.displayImage || capture;
    // Native AVIF/WebP breaks OpenCV face-lock on the worker — normalize once on entry.
    (async () => {
      let applyJpeg = rawApply;
      let displayJpeg = rawDisplay;
      try {
        applyJpeg = await ensureJpegDataUrl(rawApply);
        displayJpeg =
          rawDisplay === rawApply ? applyJpeg : await ensureJpegDataUrl(rawDisplay);
      } catch {
        /* keep original if re-encode fails */
      }
      if (cancelled) return;
      originalCaptureRef.current = applyJpeg;
      handoffApplyBaseRef.current =
        studioFocus === 'color' && isHairColorHandoffFromLook(location.state) ? applyJpeg : '';
      handoffStyleBaseRef.current =
        studioFocus === 'style' && shouldApplyHairstyleOnCurrentLook(location.state) ? applyJpeg : '';
      setSourceImage(displayJpeg);
      setResultImage('');
    })();
    return () => {
      cancelled = true;
    };
  }, [location.state, studioFocus]);

  const findAppliedByStyleId = useCallback(
    (styleId, colorId = 'natural') => {
      const id = styleId != null ? String(styleId) : '';
      if (!id) return null;
      const colorKey = String(colorId || 'natural');
      return (
        appliedLookHistory.find(
          (item) =>
            item.styleId
            && String(item.styleId) === id
            && String(item.colorId || 'natural') === colorKey,
        ) || null
      );
    },
    [appliedLookHistory],
  );

  const findAppliedByColorId = useCallback(
    (colorId, region = 'full') => {
      const id = colorId != null ? String(colorId) : '';
      if (!id) return null;
      const regionKey = String(region || 'full');
      return (
        appliedLookHistory.find(
          (item) =>
            item.colorId
            && String(item.colorId) === id
            && String(item.colorRegion || 'full') === regionKey,
        ) || null
      );
    },
    [appliedLookHistory],
  );

  const reuseAppliedLook = useCallback((entry, { style = null, color = null } = {}) => {
    if (!entry?.image) return false;
    setResultImage(entry.image);
    setComparing(false);
    setError('');
    if (style) setSelectedStyle(style);
    if (color) setSelectedColor(color);
    setCompareLeftId(COMPARE_ORIGINAL_ID);
    setCompareRightId(entry.id);
    setComparePickSlot('right');
    return true;
  }, []);

  useEffect(() => {
    const entry = resolveMakeupStyleStudioEntry(location.state);
    if (!entry.displayImage) {
      navigate(appPath('capture'), { replace: true });
    }
  }, [appPath, location.state, navigate]);

  const hydrateStylesForGender = useCallback(async (gender, profile = null) => {
    const g = normalizeGender(gender);
    if (!g) return [];
    const view = headViewFromProfile(profile || hairProfile);
    let list = getPromptStylesForGender(g);
    if (view) list = filterPromptStylesForHeadView(list, view);
    const loaded = list.map((s) => ({
      ...s,
      thumbnail_url: s.thumbnail_url || promptStyleThumbUrl(s.id),
    }));
    setStyles(loaded);
    return loaded;
  }, [hairProfile]);

  // Load men/women catalog as soon as presentation gender is known.
  useEffect(() => {
    if (studioFocus !== 'style') return undefined;
    const g = normalizeGender(resolvedGender || seededGender || '');
    if (!g) return undefined;
    let cancelled = false;
    (async () => {
      const loaded = await hydrateStylesForGender(g);
      if (cancelled || !loaded.length) return;
    })();
    return () => {
      cancelled = true;
    };
  }, [hydrateStylesForGender, resolvedGender, seededGender, studioFocus]);

  const applyRecommendResponse = useCallback(
    async (data) => {
      const profile = data.hair_profile || null;
      setHairProfile(profile);
      const gender = normalizeGender(
        data.gender || data.gender_hint || profile?.presentation || '',
      );
      if (gender) {
        setResolvedGender(gender);
        setGenderSource(data.source || profile?.source || 'gemma_vision');
        if (studioFocus === 'style') {
          await hydrateStylesForGender(gender, profile);
        }
      }
      if (studioFocus === 'style') {
        const matchedColor = matchPromptHairColorFromProfile(profile);
        if (matchedColor) setPromptHairColor(matchedColor);
      }
      let recs;
      if (studioFocus === 'style' && gender) {
        recs = recommendPromptStyles(gender, profile, 6);
        setRecommendations(recs);
        if (recs.length) setTab('recommended');
      } else {
        const raw = data.recommendations || [];
        recs = raw
          .filter((r) => r.type === 'hairstyle' || (r.style_id && !r.color_id))
          .slice(0, 4);
        setRecommendations(recs);
        if (recs.length) {
          setTab('recommended');
          const alreadyOpened = recModalAutoOpenedForImageRef.current === sourceImage;
          if (!alreadyOpened && !recModalUserClosedRef.current) {
            recModalAutoOpenedForImageRef.current = sourceImage || 'open';
            setStylesFullViewOpen(true);
          }
        }
      }
      return recs;
    },
    [hydrateStylesForGender, sourceImage, studioFocus],
  );

  // Session gender is not authoritative for hairstyle catalog — wait for Gemma photo analysis.
  useEffect(() => {
    if (seededGender && !sourceImage) {
      setResolvedGender(seededGender);
      setGenderSource('session');
    }
  }, [seededGender, sourceImage]);

  useEffect(() => {
    if (studioFocus === 'color') {
      setRecommendations([]);
      setTab('color');
      return undefined;
    }
    return undefined;
  }, [studioFocus]);

  // Hairstyle test studio: local name catalog only (FLUX prompt = style name).
  useEffect(() => {
    if (!sourceImage || studioFocus !== 'style') return undefined;
    const g = normalizeGender(resolvedGender || seededGender || '');
    if (!g) return undefined;
    void hydrateStylesForGender(g, hairProfile);
    return undefined;
  }, [sourceImage, studioFocus, resolvedGender, seededGender, hairProfile, hydrateStylesForGender]);

  // After capture: Gemma vision → gender, hair profile, prompt-catalog recommendations.
  useEffect(() => {
    if (!sourceImage || studioFocus === 'color') return undefined;
    recModalAutoOpenedForImageRef.current = '';
    recModalUserClosedRef.current = false;
    let cancelled = false;
    let progressTimer = null;
    setLoaderTitle(HAIR_RECOMMEND_TITLE);
    setHairRecommendLoaderStep(setLoaderProgress, setLoaderMessage, 0);
    startBusy('recommend', '');
    const started = Date.now();
    progressTimer = window.setInterval(() => {
      const elapsed = Date.now() - started;
      const eased = Math.min(92, Math.round(92 * (1 - Math.exp(-elapsed / 16000))));
      if (!cancelled) {
        setLoaderProgress(eased);
        setLoaderMessage(hairRecommendMessageForProgress(eased));
      }
    }, 140);

    (async () => {
      let recommendOk = false;
      try {
        const data = await recommendHairLooks({
          image: sourceImage || getOriginalCapture(),
          scope: 'style',
        });
        if (cancelled) return;
        const recs = await applyRecommendResponse(data);
        recommendOk = true;
        const gotGender = normalizeGender(data.gender || data.gender_hint || '');
        if ((data.needs_gender || !gotGender) && studioFocus === 'style' && !cancelled) {
          setError('Choose Men or Women to see recommended templates for this photo.');
        } else if (!recs.length) {
          setError('No recommended styles for this photo');
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'Could not load recommended hairstyles');
        }
      } finally {
        if (cancelled) return;
        window.clearInterval(progressTimer);
        if (recommendOk) {
          await finishLoaderUi(setLoaderProgress, setLoaderMessage, HAIR_RECOMMEND_DONE);
        }
        if (!cancelled) stopBusy();
      }
    })();
    return () => {
      cancelled = true;
      if (progressTimer) window.clearInterval(progressTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceImage, studioFocus]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (studioFocus !== 'color') return;
      try {
        const c = await fetchHairColors();
        if (cancelled) return;
        setColors(c.colors || []);
        if (c.techniques?.length) setColorTechniques(c.techniques);
      } catch (err) {
        if (!cancelled) setError(err.message || 'Could not load catalogs');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [studioFocus]);

  const loadRecommendations = useCallback(async () => {
    if (studioFocus === 'color') return;
    if (!sourceImage) {
      setError('Capture a photo first');
      return;
    }
    setLoaderTitle(HAIR_RECOMMEND_TITLE);
    setHairRecommendLoaderStep(setLoaderProgress, setLoaderMessage, 0);
    startBusy('recommend', '');
    const started = Date.now();
    const progressTimer = window.setInterval(() => {
      const elapsed = Date.now() - started;
      const eased = Math.min(92, Math.round(92 * (1 - Math.exp(-elapsed / 16000))));
      setLoaderProgress(eased);
      setLoaderMessage(hairRecommendMessageForProgress(eased));
    }, 140);
    let recommendOk = false;
    try {
      const data = await recommendHairLooks({
        image: getOriginalCapture() || sourceImage,
        // Manual Men/Women only — otherwise Gemma decides from the photo.
        gender_hint: genderSource === 'manual' ? (resolvedGender || undefined) : undefined,
        gender: genderSource === 'manual' ? (resolvedGender || undefined) : undefined,
        scope: 'style',
      });
      const recs = await applyRecommendResponse(data);
      recommendOk = true;
      if (!recs.length) {
        setError('No recommended styles for this photo');
      }
    } catch (err) {
      setError(err.message || 'Could not load recommended hairstyles');
    } finally {
      window.clearInterval(progressTimer);
      if (recommendOk) {
        await finishLoaderUi(setLoaderProgress, setLoaderMessage, HAIR_RECOMMEND_DONE);
      }
      stopBusy();
    }
  }, [resolvedGender, sourceImage, studioFocus, applyRecommendResponse, genderSource, getOriginalCapture]);

  useEffect(() => {
    if (!jobId) return undefined;
    let cancelled = false;
    const loaderKind =
      busyKind === 'color' || pendingApplySourceRef.current === 'color' ? 'color' : busyKind || 'style';
    const msgFn = applyLoaderMessageFn(loaderKind);

    const poll = async () => {
      try {
        const data = await fetchHairJobStatus(jobId);
        if (cancelled) return;
        const job = data.job || {};
        const label = STATUS_LABELS[job.status] || job.status || '';
        setStatusText(label);
        const floor = hairJobProgressFloor(job.status);
        setLoaderProgress((p) => {
          const next = Math.max(p, floor);
          setLoaderMessage(msgFn(next));
          return next;
        });
        if (job.status === 'processing' && !jobLoaderCreepRef.current) {
          startJobLoaderCreep(loaderKind);
        }
        if (job.face_lock && typeof job.face_lock === 'object') {
          setPipelineDiag(job.face_lock);
          if (job.status === 'completed') {
            setShowPipelineDiag(true);
          }
        }
        if (job.status === 'completed') {
          if (jobLoaderCreepRef.current) {
            window.clearInterval(jobLoaderCreepRef.current);
            jobLoaderCreepRef.current = null;
          }
          const applyResult = (imageUrl) => {
            setResultImage(imageUrl);
            rememberAppliedLook(imageUrl, {
              label: pendingApplyLabelRef.current,
              source: pendingApplySourceRef.current,
              styleId: pendingApplyStyleIdRef.current,
              colorId: pendingApplyColorIdRef.current,
              colorRegion: pendingApplyColorRegionRef.current,
              colorName: pendingApplyColorNameRef.current,
            });
          };
          let loaded = false;
          const rid = job.id || jobId;
          if (rid) {
            try {
              const objectUrl = await fetchHairResultObjectUrl(rid);
              applyResult(objectUrl);
              loaded = true;
            } catch (err) {
              if (job.result_path && String(job.result_path).startsWith('data:')) {
                applyResult(job.result_path);
                loaded = true;
              } else {
                setError(err?.message || 'Could not load generated image');
              }
            }
          }
          if (!loaded && job.result_path && String(job.result_path).startsWith('data:')) {
            applyResult(job.result_path);
            loaded = true;
          }
          if (loaded) {
            setLoaderProgress(96);
            setLoaderMessage(msgFn(96));
            await finishLoaderUi(setLoaderProgress, setLoaderMessage, HAIR_APPLY_DONE);
            stopBusy();
            setJobId('');
          } else if (!job.result_url && !job.result_path) {
            setError('Generation finished but no result image was returned');
            stopBusy();
            setJobId('');
          }
        } else if (job.status === 'failed') {
          setError(job.error_message || 'Generation failed');
          stopBusy();
          setJobId('');
        }
      } catch {
        /* ignore */
      }
    };
    poll();
    const t = window.setInterval(poll, 800);
    return () => {
      cancelled = true;
      window.clearInterval(t);
      if (jobLoaderCreepRef.current) {
        window.clearInterval(jobLoaderCreepRef.current);
        jobLoaderCreepRef.current = null;
      }
    };
  }, [jobId, rememberAppliedLook, busyKind, applyLoaderMessageFn, startJobLoaderCreep]);

  useEffect(() => {
    document.body.classList.toggle('hair-fullscreen-active', isFullscreen);
    return () => document.body.classList.remove('hair-fullscreen-active');
  }, [isFullscreen]);

  const runGenerate = async (mode, overrides = {}) => {
    const baseImage = getOriginalCapture();
    if (!baseImage) {
      setError('Capture a photo first');
      return;
    }

    const styleForJob = overrides.style || selectedStyle;
    const colorsForJob =
      overrides.colors && overrides.colors.length
        ? overrides.colors
        : overrides.color
          ? [overrides.color]
          : multiColorEnabled && selectedColors.length
            ? selectedColors
            : selectedColor
              ? [selectedColor]
              : [];
    const colorForJob = colorsForJob[0] || overrides.color || selectedColor;
    const wantStyle =
      mode === 'hairstyle'
      || (studioFocus === 'style' && (mode === 'custom_prompt' || mode === 'hairstyle'));

    // Hairstyle try-on (styling hub, or Recommended styles inside coloring hub).
    if (wantStyle) {
      const isCustom = mode === 'custom_prompt' && studioFocus === 'style';
      if (studioFocus === 'style' && !resolvedGender) {
        setError('Choose Men or Women (or wait for photo analysis) before applying a style.');
        return;
      }
      const basePrompt = isCustom
        ? customPrompt.trim()
        : String(styleForJob?.prompt || styleForJob?.name || '').trim();
      const promptText = buildPromptWithHairColor(basePrompt, promptHairColor);
      if (!isCustom && !styleForJob?.id) {
        setError('Select a hairstyle first');
        return;
      }
      if (isCustom && basePrompt.length < 2) {
        setError('Enter a hairstyle prompt (at least 2 characters)');
        return;
      }
      if (styleForJob) setSelectedStyle(styleForJob);
      if (!isCustom && styleForJob?.id) {
        const cached = findAppliedByStyleId(styleForJob.id, promptHairColor?.id);
        if (cached) {
          reuseAppliedLook(cached, { style: styleForJob });
          return;
        }
      }
      pendingApplyLabelRef.current = styleForJob?.name || (isCustom ? 'Custom prompt' : 'Hairstyle');
      pendingApplySourceRef.current = isCustom ? 'custom' : 'style';
      pendingApplyStyleIdRef.current = isCustom ? null : styleForJob?.id || null;
      pendingApplyColorIdRef.current = null;
      pendingApplyColorRegionRef.current = null;
      pendingApplyColorNameRef.current =
        promptHairColor?.id === 'natural' ? '' : promptHairColor?.name || '';
      setError('');
      setPipelineDiag(null);
      setShowPipelineDiag(false);
      const applyKind = isCustom ? 'custom' : 'style';
      startBusy(applyKind, '');
      beginApplyLoader(applyKind);
      setHairStyleApplyLoaderStep(setLoaderProgress, setLoaderMessage, 16);
      try {
        setHairStyleApplyLoaderStep(setLoaderProgress, setLoaderMessage, 28);
        setStatusText(hairStyleApplyMessageForProgress(28));
        const targetLength = isCustom
          ? targetHairLengthForStyle({ name: basePrompt, prompt: basePrompt, category: '' })
          : targetHairLengthForStyle(styleForJob);
        const guestLength = String(hairProfile?.current_length || '').trim();
        setHairStyleApplyLoaderStep(setLoaderProgress, setLoaderMessage, 36);
        setStatusText(hairStyleApplyMessageForProgress(36));
        startJobLoaderCreep(applyKind);
        const data = await generateHairPromptLook({
          image: baseImage,
          prompt: isCustom ? promptText : '',
          wrapIdentity: true,
          styleId: isCustom ? undefined : styleForJob?.id,
          hairLength: targetLength || undefined,
          guestCurrentLength: guestLength || undefined,
          hairProfile: hairProfile || undefined,
          genderHint: resolvedGender || undefined,
          keepOriginalColor: promptHairColor?.id === 'natural',
          promptHairColor,
        });
        if (data.image) {
          if (jobLoaderCreepRef.current) {
            window.clearInterval(jobLoaderCreepRef.current);
            jobLoaderCreepRef.current = null;
          }
          setResultImage(data.image);
          rememberAppliedLook(data.image, {
            label: pendingApplyLabelRef.current,
            source: pendingApplySourceRef.current,
            styleId: pendingApplyStyleIdRef.current,
            colorId: promptHairColor?.id === 'natural' ? null : promptHairColor?.id || null,
            colorName: promptHairColor?.id === 'natural' ? '' : promptHairColor?.name || '',
          });
          setStatusText(hairStyleApplyMessageForProgress(94));
          setHairStyleApplyLoaderStep(setLoaderProgress, setLoaderMessage, 94);
          await finishLoaderUi(setLoaderProgress, setLoaderMessage, HAIR_APPLY_DONE);
          stopBusy();
          return;
        }
        stopBusy();
        setError('Could not generate a preview. Please try again.');
      } catch (err) {
        setError(err.message || 'Generation failed');
        stopBusy();
      }
      return;
    }

    // Hair Coloring hub only below.
    if (studioFocus !== 'color') {
      setError('Select a hairstyle first');
      return;
    }

    if (!colorsForJob.length) {
      setError('Select a hair colour first');
      return;
    }
    if (colorsForJob.length === 1) setSelectedColor(colorsForJob[0]);
    if (colorsForJob.length > 1) setSelectedColors(colorsForJob);

    const techniqueId = String(overrides.colorTechnique || colorTechnique || 'global').trim().toLowerCase();
    const needsBaseTarget = TECHNIQUES_NEED_BASE_TARGET.has(techniqueId);
    let baseForJob = null;
    let targetForJob = colorForJob;
    let accentForJob = null;
    if (colorsForJob.length >= 3) {
      [baseForJob, targetForJob, accentForJob] = colorsForJob;
    } else if (colorsForJob.length === 2) {
      baseForJob = colorsForJob[0];
      targetForJob = colorsForJob[1];
    } else if (needsBaseTarget && colorsForJob.length === 1) {
      setError('Select base + target colours (enable Multi-color, pick two swatches)');
      stopBusy();
      return;
    }

    if (targetForJob?.id) {
      const cached = findAppliedByColorId(targetForJob.id, techniqueId);
      if (cached) {
        reuseAppliedLook(cached, { color: targetForJob });
        return;
      }
    }
    pendingApplyLabelRef.current =
      colorsForJob.length > 1
        ? colorsForJob.map((c) => c.name).join(' + ')
        : colorForJob?.name || 'Hair colour';
    pendingApplySourceRef.current = 'color';
    pendingApplyStyleIdRef.current = null;
    pendingApplyColorIdRef.current = colorForJob?.id || null;
    pendingApplyColorRegionRef.current = techniqueId;
    pendingApplyColorNameRef.current = colorsForJob.map((c) => c.name).filter(Boolean).join(', ');
    setError('');
    const applyKind = 'color';
    startBusy(applyKind, '');
    beginApplyLoader(applyKind);
    setHairColorApplyLoaderStep(setLoaderProgress, setLoaderMessage, 16);
    try {
      // Always colour from the original capture — never stack on a prior result.
      setStatusText('Recolouring…');
      setHairColorApplyLoaderStep(setLoaderProgress, setLoaderMessage, 28);
      const colorLabel =
        colorsForJob.length > 1
          ? colorsForJob.map((c) => c.name).join(', ')
          : colorForJob?.name || 'new color';
      const data = await generateHairColorLook(
        buildGeneratePayload({
          mode: 'hair_color',
          color_id: targetForJob?.id,
          color_ids: colorsForJob.map((c) => c.id),
          color_technique: techniqueId,
          base_color_id: baseForJob?.id,
          highlight_color_id: targetForJob?.id,
          accent_color_id: accentForJob?.id,
          prompt: `Salon hair colour (${techniqueId.replace(/_/g, ' ')}) — ${colorLabel}. Same haircut and length; dye only.`,
          color_region: 'full',
          aspect_ratio: 'source',
          gender_hint: resolvedGender || undefined,
          color_flux_only: true,
          color_composite: false,
        }),
      );
      if (data.image) {
        setResultImage(data.image);
        rememberAppliedLook(data.image, {
          label: pendingApplyLabelRef.current,
          source: pendingApplySourceRef.current,
          styleId: null,
          colorId: pendingApplyColorIdRef.current,
          colorRegion: techniqueId,
          colorName: pendingApplyColorNameRef.current,
        });
        setHairColorApplyLoaderStep(setLoaderProgress, setLoaderMessage, 94);
        await finishLoaderUi(setLoaderProgress, setLoaderMessage, HAIR_COLOR_APPLY_DONE);
        stopBusy();
        return;
      }
      if (data.job_id) {
        setJobId(data.job_id);
        setStatusText(STATUS_LABELS.queued);
        setHairColorApplyLoaderStep(setLoaderProgress, setLoaderMessage, 22);
        startJobLoaderCreep(applyKind);
        if (data.code === 'HAIR_MODEL_MISSING') {
          setError(data.error || 'Hair AI model is not installed yet');
          stopBusy();
          setJobId('');
        }
        return;
      }
      stopBusy();
    } catch (err) {
      setError(err.message || 'Generation failed');
      stopBusy();
    }
  };

  const colorsForApply = useCallback(() => {
    if (multiColorEnabled && selectedColors.length) return selectedColors;
    if (selectedColor) return [selectedColor];
    return [];
  }, [multiColorEnabled, selectedColors, selectedColor]);

  const toggleCatalogColor = useCallback(
    (c) => {
      if (multiColorEnabled) {
        setSelectedColors((prev) => {
          const has = prev.some((x) => x.id === c.id);
          if (has) return prev.filter((x) => x.id !== c.id);
          if (prev.length >= 3) {
            setError('You can select up to 3 colors');
            return prev;
          }
          setError('');
          return [...prev, c];
        });
        setSelectedColor(null);
      } else {
        setSelectedColor(c);
        setSelectedColors([]);
        setError('');
      }
    },
    [multiColorEnabled],
  );

  const applySelected = () => {
    if (studioFocus === 'color') {
      const picked = colorsForApply();
      if (!picked.length) {
        setError('Select a hair colour first');
        return;
      }
      if (picked.length > 1) {
        setMultiColorConfirmOpen(true);
        return;
      }
      void runGenerate('hair_color', {
        color: picked[0],
        colors: picked,
      });
      return;
    }
    if (!selectedStyle) {
      setError('Select a hairstyle first');
      return;
    }
    void runGenerate('hairstyle', { style: selectedStyle });
  };

  const canApply =
    studioFocus === 'color'
      ? colorsForApply().length > 0
      : tab === 'recommended'
        ? Boolean(selectedStyle)
        : Boolean(selectedStyle);

  const resolveStyleFromRec = useCallback(
    (rec) => {
      if (!rec) return null;
      const byId = rec.style_id ? styles.find((x) => x.id === rec.style_id) : null;
      if (byId) return byId;
      const byName = rec.name ? styles.find((x) => x.name === rec.name) : null;
      if (byName) return byName;
      if (rec.style_id || rec.name) {
        return {
          id: rec.style_id || `rec_${rec.name}`,
          name: rec.name,
          prompt: rec.prompt || rec.description || rec.name,
          thumbnail_url: rec.thumbnail_url || promptStyleThumbUrl(rec.style_id),
          placeholder_url: rec.placeholder_url,
        };
      }
      return null;
    },
    [styles],
  );

  const selectRecommendedStyle = useCallback(
    (rec) => {
      const styleFromRec = resolveStyleFromRec(rec);
      if (!styleFromRec?.id) {
        setError('Style recommendation unavailable');
        return;
      }
      setSelectedStyle(styleFromRec);
      setError('');
      setStylesFullViewOpen(false);
    },
    [resolveStyleFromRec],
  );

  const closeStylesFullView = useCallback(() => {
    recModalUserClosedRef.current = true;
    setStylesFullViewOpen(false);
  }, []);

  const toggleStylesFullView = useCallback(() => {
    setStylesFullViewOpen((open) => {
      if (open) recModalUserClosedRef.current = true;
      return !open;
    });
  }, []);

  const hairStylesFullViewTitle = useMemo(() => {
    const label = STYLE_TABS.find((t) => t.id === tab)?.label || 'Styles';
    return `Hairstyle · ${label}`;
  }, [tab]);

  const catalogHeadView = useMemo(() => headViewFromProfile(hairProfile), [hairProfile]);

  const styleGroups = useMemo(() => {
    if (studioFocus !== 'style') return [];
    const g = normalizeGender(resolvedGender || '');
    const groups = getPromptStyleGroups(g);
    return filterStyleGroupsForHeadView(groups, catalogHeadView);
  }, [catalogHeadView, resolvedGender, studioFocus]);

  useEffect(() => {
    if (!selectedStyle || !catalogHeadView) return;
    if (!styleFitsHeadView(selectedStyle, catalogHeadView)) {
      setSelectedStyle(null);
      setError('That style needs a different photo angle — pick one suited to this view.');
    }
  }, [catalogHeadView, selectedStyle]);

  const renderStyleNameChip = (style, { selected, onPick, key }) => {
    const thumb = hairStyleThumbUrl(style.thumbnail_url || promptStyleThumbUrl(style.id));
    return (
      <li key={key || style.id} className={selected ? 'is-selected' : ''}>
        <button
          type="button"
          className="hair-studio__name-chip"
          disabled={busy}
          aria-label={style.name || 'Hairstyle'}
          title={style.name || ''}
          onClick={onPick}
        >
          {thumb ? (
            <img
              className="hair-studio__name-chip-img"
              src={thumb}
              alt=""
              loading="lazy"
              decoding="async"
              onError={(e) => {
                const fb = promptStyleThumbFallbackUrl(style.id);
                if (fb && e.currentTarget.src !== fb) {
                  e.currentTarget.src = fb;
                  return;
                }
                e.currentTarget.style.display = 'none';
              }}
            />
          ) : null}
          <span className="hair-studio__name-chip-label">{style.name || 'Style'}</span>
        </button>
      </li>
    );
  };

  const renderHairStyleTabPanel = useCallback(() => {
    if (studioFocus !== 'style') return null;
    if (tab === 'recommended') {
      return (
        <div className="hair-studio__stack">
          {hairProfile ? (
            <p className="hair-studio__hint" role="status">
              Analysis
              {resolvedGender ? ` · ${resolvedGender === 'men' ? 'Men' : 'Women'}` : ''}
              {catalogHeadView
                ? ` · ${headViewDisplayLabel(catalogHeadView) || catalogHeadView}`
                : ''}
              {hairProfile.face_shape ? ` · ${hairProfile.face_shape} face` : ''}
              {hairProfile.current_length && hairProfile.current_length !== 'unknown'
                ? ` · ${hairProfile.current_length} hair`
                : ''}
              {hairProfile.coverage && hairProfile.coverage !== 'full'
                ? ` · ${String(hairProfile.coverage).replace(/_/g, ' ')}`
                : ''}
              {hairProfile.salon_note ? ` — ${hairProfile.salon_note}` : ''}
            </p>
          ) : (
            <p className="hair-studio__hint">Upload a photo to run Gemma vision analysis.</p>
          )}
          {!recommendations.length ? (
            <p className="hair-studio__hint">
              {busy && busyKind === 'recommend'
                ? 'Analyzing your photo…'
                : 'No recommendations yet. Use Men/Women above if detection is unclear, or refresh.'}
            </p>
          ) : (
            <ul className="hair-studio__name-grid" aria-label="Recommended hairstyles">
              {recommendations.map((rec) => {
                const style = resolveStyleFromRec(rec);
                if (!style) return null;
                return renderStyleNameChip(style, {
                  key: style.id,
                  selected:
                    selectedStyle?.id === style.id
                    || (!style.id && selectedStyle?.name === style.name),
                  onPick: () => selectRecommendedStyle(rec),
                });
              })}
            </ul>
          )}
          {recommendations
            .filter((rec) => rec.reason)
            .map((rec) => (
              <p
                key={`${rec.style_id || rec.name}-reason`}
                className="hair-studio__hint hair-studio__rec-reason"
              >
                <strong>{rec.name}:</strong> {rec.reason}
              </p>
            ))}
          <button
            type="button"
            className="hair-studio__secondary"
            disabled={busy || !sourceImage}
            onClick={() => void loadRecommendations()}
          >
            Refresh analysis
          </button>
        </div>
      );
    }
    if (tab === 'catalog') {
      return (
        <div className="hair-studio__stack">
          {!resolvedGender ? (
            <p className="hair-studio__hint">Choose Men or Women above to load the style list.</p>
          ) : null}
          {resolvedGender && catalogHeadView ? (
            <p className="hair-studio__hint" role="status">
              Styles match your photo ({headViewDisplayLabel(catalogHeadView)}). Updos and back braids
              are hidden on front-facing photos where they rarely render well.
            </p>
          ) : null}
          {styleGroups.map((group) => (
            <section key={group.category} className="hair-studio__style-section">
              <h3 className="hair-studio__style-section-title">{group.category}</h3>
              <ul className="hair-studio__name-grid" aria-label={group.category}>
                {group.names.map((name) => {
                  const style = styles.find((s) => s.name === name) || {
                    id: `tmp_${name}`,
                    name,
                    prompt: name,
                  };
                  return renderStyleNameChip(style, {
                    key: style.id,
                    selected: (() => {
                      const hit = styles.find((s) => s.name === name) || style;
                      if (!selectedStyle) return false;
                      if (hit.id && selectedStyle.id) {
                        return String(selectedStyle.id) === String(hit.id);
                      }
                      return selectedStyle.name === hit.name;
                    })(),
                    onPick: () => {
                      const hit = styles.find((s) => s.name === name) || style;
                      setSelectedStyle(hit);
                      setError('');
                      setStylesFullViewOpen(false);
                    },
                  });
                })}
              </ul>
            </section>
          ))}
        </div>
      );
    }
    return null;
  }, [
    studioFocus,
    tab,
    hairProfile,
    resolvedGender,
    catalogHeadView,
    recommendations,
    busy,
    busyKind,
    sourceImage,
    resolveStyleFromRec,
    selectedStyle,
    selectRecommendedStyle,
    loadRecommendations,
    styleGroups,
    styles,
  ]);

  const onPanelHandlePointerDown = (event) => {
    if (!isKioskViewport || isFullscreen) return;
    panelDragRef.current = { startY: event.clientY, dragging: true, moved: false };
    try {
      event.currentTarget.setPointerCapture?.(event.pointerId);
    } catch {
      /* ignore */
    }
  };

  const onPanelHandlePointerMove = (event) => {
    if (!panelDragRef.current.dragging) return;
    if (Math.abs(event.clientY - panelDragRef.current.startY) > 6) {
      panelDragRef.current.moved = true;
    }
  };

  const onPanelHandlePointerUp = (event) => {
    const { startY, dragging, moved } = panelDragRef.current;
    if (!dragging) return;
    panelDragRef.current.dragging = false;
    if (!isKioskViewport || isFullscreen) return;
    const deltaY = event.clientY - startY;
    if (deltaY > 36) setPanelOpen(false);
    else if (deltaY < -36) setPanelOpen(true);
    else if (!moved) setPanelOpen((prev) => !prev);
  };

  const onPanelHandlePointerCancel = () => {
    panelDragRef.current.dragging = false;
  };

  const onToggleCompare = () => {
    if (!appliedLookHistory.length) return;
    setComparing((prev) => {
      const next = !prev;
      if (next) {
        setCompareLeftId(COMPARE_ORIGINAL_ID);
        setCompareRightId((cur) => {
          if (cur && (cur === COMPARE_ORIGINAL_ID || appliedLookHistory.some((l) => l.id === cur))) {
            return cur;
          }
          return appliedLookHistory[0]?.id || null;
        });
        setComparePickSlot('right');
        setComparePosition(50);
      }
      return next;
    });
  };

  const onSelectCompareLook = useCallback(
    (lookId) => {
      handleCompareLookSelect({
        lookId,
        comparePickSlot,
        compareLeftId,
        compareRightId,
        setCompareLeftId,
        setCompareRightId,
        setComparePickSlot,
      });
    },
    [comparePickSlot, compareLeftId, compareRightId],
  );

  const openDownloadQr = useCallback(
    (lookItem) => {
      if (lookItem?.image) {
        setQrImageOverride(lookItem.image);
        setQrLabelOverride(lookItem.label || (studioFocus === 'color' ? 'Hair color' : 'Hairstyle'));
      } else {
        setQrImageOverride('');
        setQrLabelOverride('');
      }
      setShowQrModal(true);
    },
    [studioFocus],
  );

  const onDownload = () => {
    if (!resultImage && !sourceImage) return;
    openDownloadQr(null);
  };

  useEffect(() => {
    if (!showQrModal) return undefined;
    let cancelled = false;
    (async () => {
      const image =
        qrImageOverride ||
        resolveStudioDownloadImage({
          comparing,
          looks: appliedLookHistory,
          originalSrc: sourceImage,
          latestResultImage: resultImage,
          sourceImage,
          comparePickSlot,
          compareLeftId,
          compareRightId,
        });
      if (!image) {
        setQrError('No hairstyle image to share');
        return;
      }
      setQrBusy(true);
      setQrError('');
      setQrDataUrl('');
      try {
        const dl = studioFocus === 'color' ? createHairColorDownloadUrl : createHairDownloadUrl;
        const url = await dl(image, studioFocus === 'color' ? 'hair-color' : 'hair-style');
        const dataUrl = await renderGuestQrDataUrl(url, 280);
        if (!cancelled) setQrDataUrl(dataUrl);
      } catch (err) {
        if (!cancelled) setQrError(err.message || 'Could not prepare QR');
      } finally {
        if (!cancelled) setQrBusy(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    showQrModal,
    qrImageOverride,
    comparing,
    appliedLookHistory,
    resultImage,
    sourceImage,
    comparePickSlot,
    compareLeftId,
    compareRightId,
    studioFocus,
  ]);

  useEffect(() => {
    if (!showQrModal) {
      setQrImageOverride('');
      setQrLabelOverride('');
    }
  }, [showQrModal]);

  const onResetConfirm = () => {
    setResultImage('');
    setComparing(false);
    setAppliedLookHistory([]);
    setCompareLeftId(COMPARE_ORIGINAL_ID);
    setCompareRightId(null);
    setSelectedStyle(null);
    setSelectedColor(null);
    setCustomPrompt('');
    setError('');
    setShowResetConfirm(false);
  };

  return (
    <div
      className={[
        'hair-studio',
        studioFocus === 'color' ? 'hair-color-studio' : '',
        isFullscreen ? 'is-fullscreen' : '',
        isKioskLayout ? 'hair-studio--kiosk' : '',
        isKioskLayout ? (panelOpen ? 'is-panel-open' : 'is-panel-collapsed') : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {!isFullscreen ? (
        <header className="hair-studio__top">
          <h1 className="hair-studio__title">{productTitle || (studioFocus === 'color' ? 'Hair Coloring' : 'Hair Styling')}</h1>
          {studioFocus === 'color' ? (
            <p className="hair-studio__lede">Try hair colours</p>
          ) : null}
          {studioFocus === 'style' && !resolvedGender ? (
            <div className="hair-studio__filters" role="group" aria-label="Choose presentation">
              <button
                type="button"
                disabled={busy}
                onClick={() => {
                  setResolvedGender('men');
                  setGenderSource('manual');
                  setError('');
                  void hydrateStylesForGender('men');
                }}
              >
                Men
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => {
                  setResolvedGender('women');
                  setGenderSource('manual');
                  setError('');
                  void hydrateStylesForGender('women');
                }}
              >
                Women
              </button>
            </div>
          ) : null}
        </header>
      ) : null}

      <div className="hair-studio__stage">
        <div className={`hair-studio__preview-wrap${comparing ? ' is-comparing' : ''}`}>
          {comparing && sourceImage && appliedLookHistory.length > 0 ? (
            <LooksCompareBoard
              originalSrc={sourceImage}
              looks={appliedLookHistory}
              leftId={compareLeftId}
              rightId={compareRightId || appliedLookHistory[0]?.id || COMPARE_ORIGINAL_ID}
              pickSlot={comparePickSlot}
              onPickSlotChange={setComparePickSlot}
              onSelectLook={onSelectCompareLook}
              onDownloadLook={openDownloadQr}
              mode={compareMode}
              onModeChange={setCompareMode}
              sliderPosition={comparePosition}
              onSliderPositionChange={setComparePosition}
              className="hair-studio__compare"
            />
          ) : displayImage ? (
            <img src={displayImage} alt="Guest preview" className="hair-studio__preview" />
          ) : (
            <p className="hair-studio__empty">No photo yet</p>
          )}

          <LooksActionsRail
            canCompare={appliedLookHistory.length > 0}
            canDownload={Boolean(resultImage || sourceImage)}
            isComparing={comparing}
            isFullscreen={isFullscreen}
            onImport={() => setPhotoModal(true)}
            onToggleCompare={onToggleCompare}
            onDownloadQr={onDownload}
            onToggleFullscreen={() => setIsFullscreen((v) => !v)}
            onRequestReset={() => setShowResetConfirm(true)}
          />

          {pipelineDiag && showPipelineDiag && studioFocus !== 'style' ? (
            <div className="hair-studio__pipeline-overlay" role="status" aria-live="polite">
              <strong>Mask &amp; composite</strong>
              <ul>
                <li>
                  Render: {pipelineDiag.flux_generated === false ? 'missing' : 'yes'}
                  {pipelineDiag.composite_applied === false ? ' · composite: no' : ' · composite: yes'}
                </li>
                <li>Engine: {pipelineDiag.engine || '—'}</li>
                <li>
                  Mask: {pipelineDiag.mask_engine || '—'}
                  {pipelineDiag.sam2_refine ? ' · SAM2 refine' : ''}
                </li>
                <li>
                  Pipeline:{' '}
                  {Array.isArray(pipelineDiag.mask_pipeline)
                    ? pipelineDiag.mask_pipeline.join(' → ')
                    : pipelineDiag.mask_pipeline || '—'}
                </li>
                <li>Mode: {pipelineDiag.replacement_mode || '—'}</li>
                <li>
                  Hair blend:{' '}
                  {pipelineDiag.hair_coverage != null
                    ? `${(Number(pipelineDiag.hair_coverage) * 100).toFixed(1)}%`
                    : '—'}
                  {pipelineDiag.flux_hair_coverage != null
                    ? ` · hair render ${(Number(pipelineDiag.flux_hair_coverage) * 100).toFixed(1)}%`
                    : ''}
                </li>
                <li>
                  Orphan erase:{' '}
                  {pipelineDiag.orphan_hair_erased != null
                    ? `${(Number(pipelineDiag.orphan_hair_erased) * 100).toFixed(2)}%`
                    : '—'}
                </li>
                <li>
                  Identity lock:{' '}
                  {pipelineDiag.identity_coverage != null
                    ? `${(Number(pipelineDiag.identity_coverage) * 100).toFixed(1)}%`
                    : '—'}
                </li>
              </ul>
              <button type="button" className="hair-studio__pipeline-close" onClick={() => setShowPipelineDiag(false)}>
                Close
              </button>
            </div>
          ) : null}

        </div>

        {busy
          ? createPortal(
              <div
                className="hair-studio__loading-overlay hair-studio__loading-overlay--ai"
                role="status"
                aria-live="polite"
              >
                <MakeupAiLoader
                  active
                  title={loaderTitle}
                  progress={loaderProgress}
                  message={loaderMessage || statusText}
                  iconSrc={loaderIconSrc}
                />
              </div>,
              document.body,
            )
          : null}

        {!isFullscreen ? (
          <div
            className={`hair-studio__panel${isKioskLayout ? ' hair-studio__panel--kiosk' : ''}${
              isKioskLayout ? (panelOpen ? ' is-open' : ' is-collapsed') : ''
            }`}
          >
            {isKioskLayout ? (
              <button
                type="button"
                className="hair-studio__panel-handle"
                aria-expanded={panelOpen}
                aria-label={panelOpen ? 'Collapse options panel' : 'Expand options panel'}
                onPointerDown={onPanelHandlePointerDown}
                onPointerMove={onPanelHandlePointerMove}
                onPointerUp={onPanelHandlePointerUp}
                onPointerCancel={onPanelHandlePointerCancel}
              />
            ) : null}

            {tabs.length > 0 ? (
              <StudioLookPickerSubTabs
                className="studio-look-picker-subtabs studio-look-picker-subtabs--light hair-studio__style-subtabs"
                tabs={STYLE_TABS}
                activeId={tab}
                disabled={busy}
                onChange={(id) => {
                  setTab(id);
                  if (isKioskLayout) setPanelOpen(true);
                }}
                trailing={
                  <StudioOptionsFullViewTrigger
                    active={stylesFullViewOpen}
                    disabled={busy}
                    onClick={toggleStylesFullView}
                  />
                }
              />
            ) : null}

            {panelOpen || !isKioskLayout ? (
              <div className="hair-studio__apply-bar">
                <button
                  type="button"
                  className="hair-studio__primary hair-studio__apply-btn"
                  disabled={busy || !canApply}
                  onClick={applySelected}
                >
                  {busy && (busyKind === 'style' || busyKind === 'color' || busyKind === 'custom')
                    ? 'Applying…'
                    : 'Apply'}
                </button>
              </div>
            ) : null}

            <div className="hair-studio__panel-body">
              {error ? <p className="hair-studio__error">{error}</p> : null}
              {pipelineDiag && studioFocus === 'style' ? (
                <div className="hair-studio__flux-prompt">
                  <button
                    type="button"
                    className="hair-studio__flux-prompt-toggle"
                    onClick={() => setShowPipelineDiag((v) => !v)}
                    aria-expanded={showPipelineDiag}
                  >
                    {showPipelineDiag ? 'Hide' : 'Show'} refinement details
                  </button>
                </div>
              ) : null}
              {studioFocus === 'style' ? (
                <>
                  <p className="hair-studio__hint">Optional hair color for your try-on</p>
                  <ul className="hair-studio__prompt-colors" aria-label="Hair color for prompt">
                    {PROMPT_HAIR_COLORS.map((c) => (
                      <li key={c.id}>
                        <button
                          type="button"
                          className={
                            promptHairColor?.id === c.id ? 'hair-studio__prompt-color is-selected' : 'hair-studio__prompt-color'
                          }
                          disabled={busy}
                          title={c.name}
                          onClick={() => setPromptHairColor(c)}
                        >
                          {c.hex ? (
                            <span className="hair-studio__prompt-color-swatch" style={{ background: c.hex }} />
                          ) : (
                            <span className="hair-studio__prompt-color-swatch hair-studio__prompt-color-swatch--natural" />
                          )}
                          <span className="hair-studio__prompt-color-label">{c.name}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </>
              ) : null}
              {renderHairStyleTabPanel()}

              {studioFocus === 'color' ? (
                <div className="hair-studio__stack">
                  <p className="hair-studio__hint">Colour technique</p>
                  <ul className="hair-studio__region-grid" aria-label="Color techniques">
                    {colorTechniques.map((t) => (
                      <li key={t.id} className={colorTechnique === t.id ? 'is-selected' : ''}>
                        <button
                          type="button"
                          className="hair-studio__region-btn"
                          onClick={() => {
                            setColorTechnique(t.id);
                            setError('');
                            if (TECHNIQUES_NEED_BASE_TARGET.has(t.id) && !multiColorEnabled) {
                              setMultiColorEnabled(true);
                              if (selectedColor && !selectedColors.length) {
                                setSelectedColors([selectedColor]);
                                setSelectedColor(null);
                              }
                            }
                          }}
                        >
                          {t.name}
                        </button>
                      </li>
                    ))}
                  </ul>
                  <label className="hair-studio__multi-color">
                    <input
                      type="checkbox"
                      checked={multiColorEnabled}
                      onChange={(e) => {
                        setMultiColorEnabled(e.target.checked);
                        if (!e.target.checked) {
                          setSelectedColors([]);
                        } else if (selectedColor) {
                          setSelectedColors([selectedColor]);
                          setSelectedColor(null);
                        }
                      }}
                    />
                    Multi-color (max 3)
                  </label>
                  <ul className="hair-studio__swatches hair-studio__swatches--rail">
                    {colors.map((c) => {
                      const active = multiColorEnabled
                        ? selectedColors.some((x) => x.id === c.id)
                        : selectedColor?.id === c.id;
                      return (
                        <li key={c.id}>
                          <button
                            type="button"
                            className={active ? 'is-selected' : ''}
                            onClick={() => toggleCatalogColor(c)}
                            title={c.name}
                          >
                            <span style={{ background: c.hex }} />
                            {c.name}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                  {multiColorEnabled ? (
                    <p className="hair-studio__hint">
                      {selectedColors.length}/3 — 1st base, 2nd target/highlight, 3rd accent (optional)
                    </p>
                  ) : (
                    <p className="hair-studio__hint">Target colour (enable multi-color for base + highlight)</p>
                  )}
                </div>
              ) : null}

            </div>
          </div>
        ) : null}
      </div>

      {photoModal ? (
        <StudioPhotoSourceModal
          open={photoModal}
          onClose={() => setPhotoModal(false)}
          onImageReady={(img) => {
            beginFreshLookCapture(img);
            handoffApplyBaseRef.current = '';
            handoffStyleBaseRef.current = '';
            originalCaptureRef.current = img;
            setSourceImage(img);
            setResultImage('');
            setComparing(false);
            setAppliedLookHistory([]);
            setCompareLeftId(COMPARE_ORIGINAL_ID);
            setCompareRightId(null);
            setPhotoModal(false);
          }}
        />
      ) : null}

      {showQrModal && typeof document !== 'undefined'
        ? createPortal(
            <div
              className="looks-qr-modal"
              onClick={() => setShowQrModal(false)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') setShowQrModal(false);
              }}
              role="presentation"
            >
              <div
                className="looks-qr-modal__card"
                role="dialog"
                aria-modal="true"
                aria-labelledby={qrTitleId}
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  type="button"
                  className="looks-qr-modal__close"
                  aria-label="Close"
                  onClick={() => setShowQrModal(false)}
                >
                  ×
                </button>
                <h3 id={qrTitleId}>Scan to download</h3>
                <p>
                  {qrLabelOverride
                    ? `Saving: ${qrLabelOverride}`
                    : comparing
                      ? 'Downloads the look on the active Left or Right slot — tap a slot label to switch.'
                      : studioFocus === 'color'
                        ? 'Scan with your phone to save this hair color.'
                        : 'Scan with your phone to save this hairstyle.'}
                </p>
                <div className="looks-qr-modal__frame">
                  {qrBusy ? (
                    <span>Preparing…</span>
                  ) : qrDataUrl ? (
                    <img src={qrDataUrl} alt="QR code to download hairstyle" />
                  ) : (
                    <span>{qrError || 'QR unavailable'}</span>
                  )}
                </div>
                {qrError && qrDataUrl ? <p className="hair-studio__error">{qrError}</p> : null}
              </div>
            </div>,
            document.body,
          )
        : null}

      {multiColorConfirmOpen && studioFocus === 'color'
        ? createPortal(
            <div className="hair-reset-overlay" role="dialog" aria-modal="true" aria-label="Confirm multi-color">
              <div className="hair-reset-modal hair-multi-color-confirm">
                <h3>Apply {colorsForApply().length} colors?</h3>
                <p>{colorsForApply().map((c) => c.name).join(' · ')}</p>
                <div className="hair-reset-modal__actions">
                  <button type="button" onClick={() => setMultiColorConfirmOpen(false)}>Cancel</button>
                  <button
                    type="button"
                    className="is-danger"
                    onClick={() => {
                      setMultiColorConfirmOpen(false);
                      const picked = colorsForApply();
                      void runGenerate('hair_color', { colors: picked });
                    }}
                  >
                    Apply & generate
                  </button>
                </div>
              </div>
            </div>,
            document.body,
          )
        : null}

      <StudioOptionsFullViewModal
        open={stylesFullViewOpen && studioFocus === 'style'}
        title={hairStylesFullViewTitle}
        onClose={closeStylesFullView}
      >
        {renderHairStyleTabPanel()}
      </StudioOptionsFullViewModal>

      {showResetConfirm
        ? createPortal(
            <div className="hair-reset-overlay" role="dialog" aria-modal="true">
              <div className="hair-reset-modal">
                <h3>Reset look?</h3>
                <p>This clears the applied look. Your photo stays.</p>
                <div className="hair-reset-modal__actions">
                  <button type="button" onClick={() => setShowResetConfirm(false)}>
                    Cancel
                  </button>
                  <button type="button" className="is-danger" onClick={onResetConfirm}>
                    Reset
                  </button>
                </div>
              </div>
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
